import { projectService } from '@ideoz/core';

/** How often to re-poll while attachments are still being indexed. */
const POLL_INTERVAL_MS = 2000;
/** Give up after this long — a stuck Celery job must not hang the dialog. */
const POLL_TIMEOUT_MS = 3 * 60 * 1000;

/** Statuses we treat as terminal failures rather than "still working". */
const FAILED_STATUSES = new Set(['FAILED', 'ERROR']);

export type ReadinessOutcome =
  | { status: 'ready' }
  | { status: 'failed'; attachmentIds: string[] }
  | { status: 'timeout' }
  | { status: 'aborted' }
  | { status: 'error'; error: unknown };

const sleep = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(timer);
      resolve();
    });
  });

/**
 * Poll `GET /attachments/ready` until the given project attachments are
 * indexed. Uploads return before the Celery worker has embedded the file, so
 * firing scope suggestion straight after an upload races the indexing and the
 * retrieval comes back empty — this is the gate that closes that race.
 */
export async function waitForAttachmentsReady(
  attachmentIds: string[],
  signal?: AbortSignal,
): Promise<ReadinessOutcome> {
  if (attachmentIds.length === 0) return { status: 'ready' };
  const deadline = Date.now() + POLL_TIMEOUT_MS;

  while (!signal?.aborted) {
    let result: { all_ready: boolean; statuses: Record<string, string | null> };
    try {
      result = await projectService.getAttachmentsReadiness(attachmentIds);
    } catch (error) {
      return { status: 'error', error };
    }
    if (signal?.aborted) break;

    if (result.all_ready) return { status: 'ready' };

    const failed = Object.entries(result.statuses)
      .filter(([, status]) => status && FAILED_STATUSES.has(status.toUpperCase()))
      .map(([id]) => id);
    if (failed.length > 0) return { status: 'failed', attachmentIds: failed };

    if (Date.now() >= deadline) return { status: 'timeout' };
    await sleep(POLL_INTERVAL_MS, signal);
  }

  return { status: 'aborted' };
}
